import {Injectable, inject} from '@angular/core';
import {LoggerService} from './logger.service';
import {LocalStorage} from '../utils/storage';
import {APP_VERSION_LABEL} from '../app-version';

export type CaptureKind = 'camera' | 'gallery';

export interface CameraFlowTrailItem {
    /** Milliseconds since the flow started */
    t: number;
    e: string;
    d?: Record<string, unknown>;
}

/**
 * State of the "change status + add photo" flow. It is kept in localStorage, because the app can be killed
 * and reloaded by the OS while the camera app is in the foreground. localStorage survives that,
 * so the user can be brought back to the dialog where he was.
 */
export interface CameraFlowState {
    flowId: string;
    userId: number | null;
    projectId: number | null;
    ttmId: number;
    statusKey: string;
    gpsLocation: { lat: number; lng: number } | null;
    heading: number | null;
    startedAt: number;
    capturing: boolean;
    captureKind: CaptureKind | null;
    captureStartedAt: number | null;
    resumeCount: number;
    trail: CameraFlowTrailItem[];
}

export interface ResumedCameraFlow extends CameraFlowState {
    /** True when the app was killed while the camera/gallery was open, so the photo is lost */
    interrupted: boolean;
}

const STORAGE_KEY = 'tsned_camera_flow';
const LOG_CATEGORY = 'CAMERA_FLOW';
/** A flow older than this is not resumed (the user has probably moved on) */
const MAX_AGE_MS = 30 * 60 * 1000;
/** Guards against a restart loop: a flow is resumed at most this many times */
const MAX_RESUMES = 2;
const MAX_TRAIL_ITEMS = 40;
/** Also log successful flows (one row each), so failures can be compared with the normal situation */
const LOG_SUCCESSFUL_FLOWS = true;
/**
 * Send a row to app_logs at every phase (capture_start, file_selected, upload_start), so the server knows how far a
 * flow got, even when the app is killed and the trail in localStorage is never sent.
 */
const LOG_PHASES = true;

@Injectable({
    providedIn: 'root'
})
export class CameraFlowService {
    private logger = inject(LoggerService);
    private deviceInfo: { model?: string; platformVersion?: string } = {};
    private detachLifecycleListeners: (() => void) | null = null;

    constructor() {
        // Chrome reduces the user agent to "Android 10; K", so the real model/OS version has to be asked for.
        try {
            (navigator as any).userAgentData?.getHighEntropyValues?.(['model', 'platformVersion'])
                .then(values => this.deviceInfo = {model: values.model, platformVersion: values.platformVersion})
                .catch(() => {
                });
        } catch (e) {
        }
    }

    /**
     * Starts the flow for a ttm, or continues the stored flow when it is the same ttm and status
     * (which is the case after a resume).
     */
    begin(init: {
        ttmId: number;
        statusKey: string;
        projectId: number | null;
        gpsLocation: { lat: number; lng: number } | null;
        heading: number | null;
    }) {
        const existing = this.read();
        if (existing && existing.ttmId === init.ttmId && existing.statusKey === init.statusKey) {
            existing.gpsLocation = existing.gpsLocation ?? init.gpsLocation;
            existing.heading = existing.heading ?? init.heading;
            this.write(existing);
            return;
        }
        this.write({
            flowId: Math.random().toString(36).slice(2, 10),
            userId: LocalStorage.user?.id ?? null,
            projectId: init.projectId,
            ttmId: init.ttmId,
            statusKey: init.statusKey,
            gpsLocation: init.gpsLocation,
            heading: init.heading,
            startedAt: Date.now(),
            capturing: false,
            captureKind: null,
            captureStartedAt: null,
            resumeCount: 0,
            trail: [],
        });
        this.track('flow_started');
    }

    /** Must be called (synchronously) before the camera/gallery is opened */
    startCapture(kind: CaptureKind, data: Record<string, unknown> = {}) {
        const state = this.read();
        if (state) {
            state.capturing = true;
            state.captureKind = kind;
            state.captureStartedAt = Date.now();
            this.write(state);
            this.track('capture_start', {kind, ...this.snapshot()});
            this.attachLifecycleListeners();
        }
        // Sent even without a stored flow: the ids passed by the caller (ttmId) do not depend on it
        this.logPhase('capture_start', {kind, ...this.snapshot(), ...data});
    }

    /** The user came back from the camera/gallery with a file */
    captureFinished(data: Record<string, unknown>) {
        this.endCapture('file_selected', data);
        this.logPhase('file_selected', data);
    }

    /** The flow that is in localStorage right now (`tsned_camera_flow`), without changing it */
    peek(): CameraFlowState | null {
        return this.read();
    }

    /**
     * The map is opened for a ttm. Only logged when a camera flow was stored in localStorage at that moment: that means
     * the app was reloaded or crashed during a flow, which ideally does not happen. Pass what peek() returned, read
     * before resume(), because resume() can change or clear the flow. Logged right away, whether or not the flow can
     * still be resumed. `navType`, `wasDiscarded` and `pageUptimeS` in the snapshot tell whether this is a fresh page load.
     */
    mapOpenedWithTtm(storedFlow: CameraFlowState | null, data: Record<string, unknown>) {
        if (!storedFlow) {
            return;
        }
        this.logger.logEvent(LOG_CATEGORY, 'map_opened_with_ttm', {
            version: APP_VERSION_LABEL,
            ...data,
            flowId: storedFlow.flowId,
            flowTtmId: storedFlow.ttmId,
            flowProjectId: storedFlow.projectId,
            flowCapturing: storedFlow.capturing,
            flowResumeCount: storedFlow.resumeCount,
            flowAgeMs: this.ageMs(storedFlow),
            ...this.snapshot(),
        });
    }

    /** upload() is called: for every file, and again for every retry after a failed upload */
    uploadCalled(data: Record<string, unknown>) {
        this.track('upload_called', data);
        this.logPhase('upload_called', data);
    }

    /** The photo is resized and the upload request is about to be sent */
    uploadStarted(data: Record<string, unknown>) {
        this.track('resized', data);
        this.logPhase('upload_start', data);
    }

    /** The picker was cancelled, or never opened */
    captureAborted(reason: string, data: Record<string, unknown> = {}) {
        this.endCapture(reason, data);
    }

    track(event: string, data?: Record<string, unknown>) {
        const state = this.read();
        if (!state) {
            return;
        }
        state.trail.push({t: Date.now() - state.startedAt, e: event, ...(data ? {d: data} : {})});
        state.trail = state.trail.slice(-MAX_TRAIL_ITEMS);
        this.write(state);
    }

    /** The photo was uploaded: the flow is finished */
    complete(data: Record<string, unknown> = {}) {
        const state = this.read();
        if (LOG_SUCCESSFUL_FLOWS) {
            this.logger.logEvent(LOG_CATEGORY, 'ok', {...this.base(state), ...data, trail: state?.trail});
        }
        this.clear();
    }

    /** Something went wrong, but the app is still alive. The flow stays so the user can retry. */
    fail(event: string, data: Record<string, unknown> = {}) {
        this.track(event, data);
        const state = this.read();
        this.logger.logEvent(LOG_CATEGORY, event, {
            ...this.base(state), ...data, ...this.snapshot(), trail: state?.trail
        });
    }

    /**
     * For a dialog that is destroyed without the user closing it explicitly (navigation, a crash): a flow that was
     * in the camera/gallery at that moment must survive, so it can be resumed.
     */
    clearUnlessCapturing() {
        if (!this.read()?.capturing) {
            this.clear();
        }
    }

    clear() {
        this.detachLifecycleListeners?.();
        this.detachLifecycleListeners = null;
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (e) {
        }
    }

    /**
     * Called when the map of a project is loaded. Returns the flow to resume, when there is a valid
     * flow for this project and user. When the app was killed while the camera was open, this is
     * logged to app_logs together with the trail that was kept in localStorage.
     */
    resume(projectId: number | null): ResumedCameraFlow | null {
        const state = this.read();
        if (!state) {
            return null;
        }
        const userId = LocalStorage.user?.id ?? null;
        const ageMs = this.ageMs(state);
        if (state.userId !== userId || ageMs > MAX_AGE_MS) {
            this.abandon(state, state.userId !== userId ? 'other_user' : 'expired', ageMs);
            return null;
        }
        if (state.projectId !== projectId) {
            // Another project is opened; keep the flow until the right project is opened (or it expires)
            return null;
        }
        if (state.resumeCount >= MAX_RESUMES) {
            this.logger.logEvent(LOG_CATEGORY, 'resume_loop_guard', {...this.base(state), trail: state.trail});
            this.clear();
            return null;
        }

        const interrupted = state.capturing;
        state.capturing = false;
        state.resumeCount++;
        this.write(state);
        this.track(interrupted ? 'resumed_after_interrupted_capture' : 'resumed');
        this.logger.logEvent(LOG_CATEGORY, interrupted ? 'interrupted' : 'resumed_idle', {
            ...this.base(state),
            ageMs,
            ...this.snapshot(),
            trail: this.read()?.trail,
        });
        return {...(this.read() ?? state), interrupted};
    }

    /**
     * Called when the app starts, on any page. A flow that is too old to be resumed is thrown away; when the
     * camera was open at that moment, this is logged so a killed app is visible even if the user never came back.
     * Flows of another user are not judged here: while logged out (e.g. to log in again) the user is not known yet,
     * and the flow must survive that.
     */
    discardExpired() {
        const state = this.read();
        if (!state) {
            return;
        }
        const ageMs = this.ageMs(state);
        if (ageMs > MAX_AGE_MS) {
            this.abandon(state, 'expired', ageMs);
        }
    }

    private ageMs(state: CameraFlowState) {
        return Date.now() - (state.captureStartedAt ?? state.startedAt);
    }

    /**
     * Throws a flow away that will not be resumed. Only a flow that was killed while the camera/gallery was open
     * is logged: a flow that was merely left in the photo step is just a user who closed the app.
     */
    private abandon(state: CameraFlowState, reason: 'expired' | 'other_user', ageMs: number) {
        if (state.capturing) {
            this.logger.logEvent(LOG_CATEGORY, 'abandoned', {
                ...this.base(state),
                userId: state.userId,
                reason,
                ageMs,
                ...this.snapshot(),
                trail: state.trail,
            });
        }
        this.clear();
    }

    private logPhase(event: string, data: Record<string, unknown> = {}) {
        if (LOG_PHASES) {
            this.logger.logEvent(LOG_CATEGORY, event, {...this.base(this.read()), ...data});
        }
    }

    private endCapture(event: string, data: Record<string, unknown>) {
        const state = this.read();
        if (state) {
            state.capturing = false;
            this.write(state);
            this.track(event, {
                ...(state.captureStartedAt ? {sinceCaptureStartMs: Date.now() - state.captureStartedAt} : {}),
                ...data
            });
        }
        this.detachLifecycleListeners?.();
        this.detachLifecycleListeners = null;
    }

    /** Page lifecycle events while the picker is open; the last of these tell what happened before an app kill */
    private attachLifecycleListeners() {
        this.detachLifecycleListeners?.();
        const onVisibility = () => this.track(document.visibilityState === 'hidden' ? 'hidden' : 'visible');
        const onPageHide = (event: PageTransitionEvent) => this.track('pagehide', {persisted: event.persisted});
        const onFreeze = () => this.track('freeze');
        const onResume = () => this.track('resume');
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', onPageHide);
        document.addEventListener('freeze', onFreeze);
        document.addEventListener('resume', onResume);
        this.detachLifecycleListeners = () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', onPageHide);
            document.removeEventListener('freeze', onFreeze);
            document.removeEventListener('resume', onResume);
        };
    }

    private base(state: CameraFlowState | null) {
        return {
            flowId: state?.flowId,
            ttmId: state?.ttmId,
            statusKey: state?.statusKey,
            projectId: state?.projectId,
            kind: state?.captureKind,
            resumeCount: state?.resumeCount,
            version: APP_VERSION_LABEL,
        };
    }

    /** Diagnostics about the device and the page; some only make sense right after a page load */
    private snapshot() {
        const memory = (performance as any).memory;
        const navigation = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
        return {
            visibility: document.visibilityState,
            pageUptimeS: Math.round(performance.now() / 1000),
            navType: navigation?.type,
            wasDiscarded: (document as any).wasDiscarded,
            online: navigator.onLine,
            standalone: window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true,
            deviceMemoryGb: (navigator as any).deviceMemory,
            cores: navigator.hardwareConcurrency,
            jsHeapUsedMb: memory ? Math.round(memory.usedJSHeapSize / 1048576) : undefined,
            jsHeapLimitMb: memory ? Math.round(memory.jsHeapSizeLimit / 1048576) : undefined,
            connection: (navigator as any).connection?.effectiveType,
            screen: `${screen.width}x${screen.height}@${window.devicePixelRatio}`,
            model: this.deviceInfo.model,
            platformVersion: this.deviceInfo.platformVersion,
        };
    }

    private read(): CameraFlowState | null {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) as CameraFlowState : null;
        } catch (e) {
            return null;
        }
    }

    private write(state: CameraFlowState) {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch (e) {
            // localStorage can be full or unavailable (private mode); the flow just won't survive a restart
        }
    }
}
